<template>
  <div class="pt-5 pb-16 md:px-5">
    <PageHeader 
      class="max-w-4xl mx-auto" 
      title="Edit Booking Address" 
      :back-route="{ name: 'BookingEdit', params: { id: $route.params.id } }" 
    />
    
    <Loading v-if="loading" class="p-6" />
    <div class="max-w-4xl mx-auto space-y-6" v-else>
      <form @submit.prevent="submit">
        <div class="card">
          <div class="card-header">
            Address
          </div>
          <div class="p-6">
            <!-- Contact -->
            <fieldset class="form-group">
              <label class="flex justify-between form-label">
                Contact
              </label>

              <div class="relative border border-gray-300 divide-y divide-gray-300 rounded shadow-sm">
                <div class="relative focus-within:z-50">
                  <input name="name" type="text" class="border-transparent rounded-none rounded-t form-input" placeholder="Company Name (If Applicable)" v-model="form.name">
                </div>
                <div class="relative focus-within:z-50">
                  <input name="contact_name" type="text" class="border-transparent rounded-none form-input" placeholder="Contact Person Name" v-model="form.contact_name">
                </div>
                <div class="relative focus-within:z-50">
                  <input type="tel" name="contact_phone" class="border-transparent rounded-none form-input" placeholder="Contact Person Phone" v-model="form.contact_phone">
                </div>
                <div class="relative focus-within:z-50">
                  <input type="email" name="contact_email" class="border-transparent rounded-none rounded-b form-input" autocomplete="disabled" placeholder="Contact Person Email (Optional)" v-model="form.contact_email">
                </div>
              </div>
              <validations :errors="errors" field="contact_name" />
              <validations :errors="errors" field="contact_email" />
              <validations :errors="errors" field="contact_phone" />
            </fieldset>

            <!-- Address -->
            <fieldset class="form-group">
              <label for="address" class="form-label">Address</label>
              <div class="relative border border-gray-300 divide-y divide-gray-300 rounded shadow-sm">
                <div class="relative focus-within:z-50">
                  <AddressAutocomplete
                    :id="`autocomplete-${form.id}`"
                    name="address"
                    ref="address"
                    input-class="border-transparent rounded-b-none form-input"
                    placeholder="Street Address"
                    @placechanged="getAddressData"
                    @inputChange="changeAddressData"
                    :initial-value="form.address_1" />
                </div>
                <div class="relative focus-within:z-50">
                  <input type="text" placeholder="Apartment/Complex/Shop No." class="border-transparent rounded-none form-input" autocomplete="disabled" v-model="form.address_2">
                </div>
                <div class="grid grid-cols-6 divide-x divide-gray-300">
                  <div class="relative col-span-4 focus-within:z-50">
                    <input type="text" placeholder="City" class="col-span-4 border-transparent rounded-none form-input" autocomplete="disabled" v-model="form.city">
                  </div>
                  <div class="relative col-span-2 focus-within:z-50">
                    <input type="text" placeholder="Zip/Postal Code" class="col-span-2 border-transparent rounded-none form-input" autocomplete="disabled" v-model="form.zip">
                  </div>
                </div>
                <div class="relative focus-within:z-50">
                  <country-select placeholder="Country or Region" class="border-transparent rounded-t-none form-select" v-model="form.country" />
                </div>
              </div>
              <validations :errors="errors" field="address_1" />
              <validations :errors="errors" field="address_2" />
              <validations :errors="errors" field="city" />
              <validations :errors="errors" field="zip" />
              <validations :errors="errors" field="country" />
              <validations :errors="errors" field="latitude" />
              <validations :errors="errors" field="longitude" />
            </fieldset>

            <!-- Notes -->
            <fieldset class="form-group">
              <label for="notes" class="form-label">
                Notes 
                <div class="ml-1 text-xs text-gray-500">Optional</div>
              </label>
              <textarea name="notes" type="text" class="form-input" autocomplete="disabled" maxlength="255" v-model="form.notes"></textarea>
              <validations :errors="errors" field="notes" />
            </fieldset>

            <!-- Reference -->
            <fieldset class="form-group">
              <label for="notes" class="form-label">
                Reference 
                <div class="ml-1 text-xs text-gray-500">Optional</div>
              </label>
              <input name="reference" type="text" class="form-input" autocomplete="disabled" v-model="form.reference">
              <validations :errors="errors" field="reference" />
            </fieldset>

            <div class="flex justify-between mt-6">
              <button class="btn btn-primary" v-submitting="submitting">Save Changes</button>
              <button class="font-medium text-red-500 hover:underline" v-submitting="submitting">Remove Address</button>
            </div>
          </div>
        </div>
      </form>

      <div class="card">
        <div class="card-header">
          Products
        </div>
        <div class="p-6">
          <ProductSearch @selected="addProduct" />
        </div>
        <div class="border-t divide-y" v-if="form.products">
          <div v-for="product in form.products" :key="product.id" class="px-6 py-4">
            <BookingAddressProduct 
              :product="product"
              :address-type="addressType"
              @updated="productUpdated"
              @deleted="productRemoved" />
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-header">
          Addons
        </div>
        <div class="p-6">
          <AddonSearch @selected="addAddon" />
        </div>
        <div class="border-t divide-y" v-if="form.addons">
          <div v-for="addon in form.addons" :key="addon.id" class="px-6 py-4">
            <BookingAddressAddon
              :addon="addon"
              :address-type="addressType"
              @deleted="addonRemoved" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AddressAutocomplete from '@/components/AddressAutocomplete'
import AddonSearch from '@/components/AddonSearch'
import ProductSearch from '@/components/ProductSearch'
import BookingAddressAddon from './BookingAddressAddon'
import BookingAddressProduct from './BookingAddressProduct'

export default {
  name: 'BookingAddressEdit',

  components: {
    AddressAutocomplete,
    AddonSearch,
    ProductSearch,
    BookingAddressAddon,
    BookingAddressProduct
  },

  async created () {
    await this.getBookingAddress()
  },

  computed: {
    addressType () {
      return this.$route.params.origin_id ? 'origin' : 'destination'
    }
  },

  data () {
    return {
      loading: true,
      submitting: false,
      form: {},
      errors: []
    }
  },

  methods: {
    async getBookingAddress () {
      try {
        this.loading = true
         if (this.addressType === 'origin') {
          const { data } = await this.$api.get(`account/${this.$route.params.account}/booking-origins/${this.$route.params.origin_id}`)
          this.form = data.origin
        } else if (this.addressType === 'destination') {
          const { data } = await this.$api.get(`account/${this.$route.params.account}/booking-destinations/${this.$route.params.destination_id}`)
          this.form = data.destination
        }
        this.loading = false
      } catch (error) {
        console.error(error)
      } 
    },

    changeAddressData (event) {
      this.form.address_1 = event.newVal
    },

    getAddressData ({ addressData, placeResultData }) {
      for (let component of placeResultData.address_components) {
        if (component.types.includes('country')) {
          this.form.country = component.short_name
        }
      }
      this.form.formatted_address = placeResultData.formatted_address
      this.form.city = addressData.locality
      this.form.zip = addressData.postal_code
      this.form.latitude = addressData.latitude
      this.form.longitude = addressData.longitude
      this.form.address_1 = placeResultData.formatted_address.split(',')[0] + ',' + placeResultData.formatted_address.split(',')[1]
      this.$refs.address.$refs.address.$el.value = this.form.address_1
    },

    async submit () {
      try {
        this.submitting = true
        if (this.addressType === 'origin') {
          await this.$api.patch(`account/${this.$route.params.account}/booking-origins/${this.form.id}`, this.form)
        } else if (this.addressType === 'destination') {
          await this.$api.patch(`account/${this.$route.params.account}/booking-destinations/${this.form.id}`, this.form)
        }
        this.$toasted.show('Address updated.')
      } catch (error) {
        console.error(error)
        this.errors = error.response.data.errors ? error.response.data.errors : []
      } finally {
        this.submitting = false
      }
    },

    async addProduct (product) {
      try {
        if (this.addressType === 'origin') {
          const { data } = await this.$api.post(`account/${this.$route.params.account}/booking-origin-products`, {
            ...product,
            quantity: 1,
            booking_origin_id: this.$route.params.origin_id   
          })
          this.form.products.push(data.product)
        } else if (this.addressType === 'destination') {
          const { data } = await this.$api.post(`account/${this.$route.params.account}/booking-destination-products`, {
            ...product,
            quantity: 1,
            booking_destination_id: this.$route.params.destination_id
          })
          this.form.products.push(data.product)
        }
      } catch (error) {
        console.error(error)
      }
    },

    productUpdated (product) {
      const index = this.form.products.findIndex(prod => prod.id === product.id)
      this.form.products[index].quantity = product.quantity
      this.form.products[index].subtotal = product.subtotal
    },

    productRemoved (product) {
      const index = this.form.products.findIndex(prod => prod.id === product.id)
      console.log(index)
      this.form.products.splice(index, 1)
    },

    async addAddon (addon) {
      try {
        if (this.addressType === 'origin') {
          const { data } = await this.$api.post(`account/${this.$route.params.account}/booking-origin-addons`, {
            ...addon,
            booking_origin_id: this.$route.params.origin_id   
          })
          this.form.addons.push(data.addon)
        } else if (this.addressType === 'destination') {
          const { data } = await this.$api.post(`account/${this.$route.params.account}/booking-destination-addons`, {
            ...addon,
            booking_destination_id: this.$route.params.destination_id
          })
          this.form.addons.push(data.addon)
        }
      } catch (error) {
        console.error(error)
      }
    },

    addonRemoved (addon) {
      const index = this.form.addons.findIndex(add => add.id === addon.id)
      this.form.addons.splice(index, 1)
    }
  }
}
</script>